import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  CircularProgress,
  useTheme,
  alpha,
  Alert
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOutMap as ZoomOutMapIcon,
  CenterFocusStrong as CenterIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import AuthenticatedImage from '../../common/AuthenticatedImage';

const DefectContextViewer = ({ workOrder }) => {
  const theme = useTheme();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Zoom and pan state for Original Image
  const [zoom, setZoom] = useState({ scale: 1, translateX: 0, translateY: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const imageContainerRef = useRef(null);

  // Reset zoom when workOrder changes
  useEffect(() => {
    setZoom({ scale: 1, translateX: 0, translateY: 0 });
    setImageLoaded(false);
    setImageError(false);
  }, [workOrder]);

  const zoomToBox = () => {
    const imageElement = imageRef.current;
    const containerElement = imageContainerRef.current;
    
    if (!imageElement || !containerElement || !workOrder) return;
    
    const imageRect = imageElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    
    const boundingBox = {
      x1: workOrder.x1,
      y1: workOrder.y1,
      x2: workOrder.x2,
      y2: workOrder.y2,
      image_width: workOrder.image_width,
      image_height: workOrder.image_height
    };

    if (!boundingBox || boundingBox.x1 === undefined) return;
    
    const boxCenterX = (boundingBox.x1 + boundingBox.x2) / 2;
    const boxCenterY = (boundingBox.y1 + boundingBox.y2) / 2;
    
    const boxWidth = boundingBox.x2 - boundingBox.x1;
    const boxHeight = boundingBox.y2 - boundingBox.y1;
    
    const paddingFactor = 0.4;
    const paddedBoxWidth = boxWidth * (1 + paddingFactor);
    const paddedBoxHeight = boxHeight * (1 + paddingFactor);
    
    const scaleX = containerRect.width / (paddedBoxWidth / (boundingBox.image_width || 1920) * imageRect.width);
    const scaleY = containerRect.height / (paddedBoxHeight / (boundingBox.image_height || 1080) * imageRect.height);
    const targetScale = Math.min(scaleX, scaleY, 4);
    
    const boxCenterXInImage = (boxCenterX / (boundingBox.image_width || 1920)) * imageRect.width;
    const boxCenterYInImage = (boxCenterY / (boundingBox.image_height || 1080)) * imageRect.height;
    
    const translateX = containerRect.width / 2 - boxCenterXInImage * targetScale;
    const translateY = containerRect.height / 2 - boxCenterYInImage * targetScale;
    
    setZoom({
      scale: targetScale,
      translateX,
      translateY
    });
  };

  const resetZoom = () => {
    setZoom({ scale: 1, translateX: 0, translateY: 0 });
  };

  const toggleZoom = () => {
    if (zoom.scale === 1) {
      zoomToBox();
    } else {
      resetZoom();
    }
  };

  const handleZoom = (delta, centerX, centerY) => {
    const zoomFactor = delta > 0 ? 1.2 : 1 / 1.2;
    const newScale = Math.max(0.5, Math.min(zoom.scale * zoomFactor, 5));
    
    if (newScale === zoom.scale) return;
    
    const scaleChange = newScale / zoom.scale;
    const newTranslateX = centerX - (centerX - zoom.translateX) * scaleChange;
    const newTranslateY = centerY - (centerY - zoom.translateY) * scaleChange;
    
    setZoom({
      scale: newScale,
      translateX: newTranslateX,
      translateY: newTranslateY
    });
  };

  const handleWheel = (event) => {
    if (!imageContainerRef.current) return;
    
    event.preventDefault();
    const rect = imageContainerRef.current.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;
    
    handleZoom(-event.deltaY, centerX, centerY);
  };

  const handlePanStart = (event) => {
    setIsPanning(true);
    setPanStart({
      x: event.clientX - zoom.translateX,
      y: event.clientY - zoom.translateY
    });
  };

  const handlePanMove = (event) => {
    if (!isPanning) return;
    
    setZoom(prev => ({
      ...prev,
      translateX: event.clientX - panStart.x,
      translateY: event.clientY - panStart.y
    }));
  };

  const handlePanEnd = () => {
    setIsPanning(false);
  };

  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handlePanMove);
      document.addEventListener('mouseup', handlePanEnd);
      
      return () => {
        document.removeEventListener('mousemove', handlePanMove);
        document.removeEventListener('mouseup', handlePanEnd);
      };
    }
  }, [isPanning, panStart]);

  if (!workOrder) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No work order data available
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {workOrder.defect_image_path ? (
          <Box
            ref={imageContainerRef}
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
              bgcolor: '#f8f9fa',
              cursor: isPanning ? 'grabbing' : (zoom.scale > 1 ? 'grab' : 'default'),
            }}
            onWheel={handleWheel}
            onMouseDown={(e) => { if (zoom.scale > 1) handlePanStart(e); }}
          >
            {!imageLoaded && !imageError && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading defect image...</Typography>
              </Box>
            )}

            {imageError && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <PhotoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">Unable to load defect image</Typography>
              </Box>
            )}

            <Box sx={{
              transform: `scale(${zoom.scale}) translate(${zoom.translateX / zoom.scale}px, ${zoom.translateY / zoom.scale}px)`,
              transformOrigin: '0 0',
              transition: isPanning ? 'none' : 'transform 0.3s ease-out',
            }}>
              <AuthenticatedImage
                ref={imageRef}
                src={workOrder.defect_image_path}
                alt="Defect Detection"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: imageError ? 'none' : 'block',
                  userSelect: 'none'
                }}
              />
              {imageLoaded && workOrder.x1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    border: `2px solid ${theme.palette.error.main}`,
                    borderRadius: '4px',
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                    pointerEvents: 'none',
                    left: `${(workOrder.x1 / (workOrder.image_width || 1)) * 100}%`,
                    top: `${(workOrder.y1 / (workOrder.image_height || 1)) * 100}%`,
                    width: `${((workOrder.x2 - workOrder.x1) / (workOrder.image_width || 1)) * 100}%`,
                    height: `${((workOrder.y2 - workOrder.y1) / (workOrder.image_height || 1)) * 100}%`,
                  }}
                />
              )}
            </Box>

            <Chip
              label="Original"
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                zIndex: 10
              }}
            />

            <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 1, zIndex: 10 }}>
              <Button
                variant="contained"
                size="small"
                onClick={toggleZoom}
                startIcon={zoom.scale === 1 ? <ZoomInIcon fontSize="small" /> : <ZoomOutMapIcon fontSize="small" />}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  fontSize: '0.75rem',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                }}
              >
                {zoom.scale === 1 ? 'Zoom to Box' : 'Reset View'}
              </Button>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.9)', px: 1, py: 0.5, borderRadius: 1, fontSize: '0.75rem', color: theme.palette.text.secondary, textAlign: 'center' }}>
                {Math.round(zoom.scale * 100)}%
              </Box>
            </Box>

            {zoom.scale > 1 && (
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                fontSize: '0.75rem',
                textAlign: 'center',
                pointerEvents: 'none',
                opacity: isPanning ? 0 : 0.8,
                transition: 'opacity 0.3s ease',
                zIndex: 5
              }}>
                Drag to pan • Scroll to zoom
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: 'text.secondary' }}>
            <PhotoIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
            <Typography variant="h6">No Defect Image Available</Typography>
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', maxWidth: 300 }}>
              Visual evidence for this defect is not available.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DefectContextViewer;